import type { SliceCreator, UiSlice, UiState } from "../types";

export const uiInitialState = (): UiState => ({
  ui: {
    themeMode: "light",
    busyKeys: [],
    selectedRequirementIds: [],
    domain: "MPR",
    assessmentSidebarIds: [],
    assessmentSidebarSelectedIds: [],
    assessmentSidebarAddedAt: {},
    assessmentSidebarVersionOverrides: {},
    assessmentSidebarOpen: false,
    assessmentSidebarCollapsed: false,
    userRole: "admin",
    privileges: [],
    privilegesLoaded: false,
  },
});

const upsertBusyKey = (keys: string[], key: string) =>
  keys.includes(key) ? keys : [...keys, key];

export const createUiSlice: SliceCreator<UiSlice> = (set) => ({
  ...uiInitialState(),
  toggleTheme: () =>
    set(
      (state) => ({
        ui: {
          ...state.ui,
          themeMode: state.ui.themeMode === "light" ? "dark" : "light",
        },
      }),
      false,
      "ui/toggleTheme",
    ),
  startBusy: (key: string) =>
    set(
      (state) => ({
        ui: {
          ...state.ui,
          busyKeys: upsertBusyKey(state.ui.busyKeys, key),
        },
      }),
      false,
      "ui/startBusy",
    ),
  stopBusy: (key: string) =>
    set(
      (state) => ({
        ui: {
          ...state.ui,
          busyKeys: state.ui.busyKeys.filter((busyKey) => busyKey !== key),
        },
      }),
      false,
      "ui/stopBusy",
    ),
  setSelectedRequirementIds: (ids: string[]) =>
    set(
      (state) => ({
        ui: {
          ...state.ui,
          selectedRequirementIds: ids,
        },
      }),
      false,
      "ui/setSelectedRequirementIds",
    ),
  setDomain: (domain: string) =>
    set(
      (state) => ({
        ui: {
          ...state.ui,
          domain,
        },
      }),
      false,
      "ui/setDomain",
    ),
  addAssessmentSidebarId: (id: string, version?: string, maxAllowed?: number) =>
    set(
      (state) => {
        const ids = state.ui.assessmentSidebarIds;
        if (ids.includes(id)) return state;
        if (
          typeof maxAllowed === "number" &&
          maxAllowed > 0 &&
          ids.length >= maxAllowed
        ) {
          return state;
        }
        const overrides = { ...state.ui.assessmentSidebarVersionOverrides };
        if (version !== undefined) overrides[id] = version;
        const addedAt = {
          ...state.ui.assessmentSidebarAddedAt,
          [id]: new Date().toISOString(),
        };

        // Figma: Adding to assessment queue should be silent (no auto-open popup).
        // Keep the floating icon visible (assessmentSidebarOpen=true), but do not force expand.
        const wasEmpty = ids.length === 0;
        const nextCollapsed = wasEmpty ? true : state.ui.assessmentSidebarCollapsed;

        return {
          ui: {
            ...state.ui,
            assessmentSidebarIds: [...ids, id],
            assessmentSidebarSelectedIds: [
              ...state.ui.assessmentSidebarSelectedIds,
              id,
            ],
            assessmentSidebarAddedAt: addedAt,
            assessmentSidebarVersionOverrides: overrides,
            assessmentSidebarOpen: true,
            assessmentSidebarCollapsed: nextCollapsed,
          },
        };
      },
      false,
      "ui/addAssessmentSidebarId",
    ),
  removeAssessmentSidebarId: (id: string) =>
    set(
      (state) => {
        const overrides = { ...state.ui.assessmentSidebarVersionOverrides };
        delete overrides[id];
        const addedAt = { ...state.ui.assessmentSidebarAddedAt };
        delete addedAt[id];
        const nextIds = state.ui.assessmentSidebarIds.filter(
          (item) => item !== id,
        );
        const nextSelectedIds = state.ui.assessmentSidebarSelectedIds.filter(
          (item) => item !== id,
        );
        return {
          ui: {
            ...state.ui,
            assessmentSidebarIds: nextIds,
            assessmentSidebarSelectedIds: nextSelectedIds,
            assessmentSidebarAddedAt: addedAt,
            assessmentSidebarVersionOverrides: overrides,
            assessmentSidebarOpen: nextIds.length > 0,
            assessmentSidebarCollapsed: nextIds.length === 0,
          },
        };
      },
      false,
      "ui/removeAssessmentSidebarId",
    ),
  clearAssessmentSidebarIds: () =>
    set(
      (state) => ({
        ui: {
          ...state.ui,
          assessmentSidebarIds: [],
          assessmentSidebarSelectedIds: [],
          assessmentSidebarAddedAt: {},
          assessmentSidebarVersionOverrides: {},
          assessmentSidebarOpen: false,
          assessmentSidebarCollapsed: true,
        },
      }),
      false,
      "ui/clearAssessmentSidebarIds",
    ),
  setAssessmentSidebarSelectedIds: (ids: string[]) =>
    set(
      (state) => {
        const queueIds = new Set(state.ui.assessmentSidebarIds);
        return {
          ui: {
            ...state.ui,
            assessmentSidebarSelectedIds: ids.filter((id) => queueIds.has(id)),
          },
        };
      },
      false,
      "ui/setAssessmentSidebarSelectedIds",
    ),
  toggleAssessmentSidebarSelectedId: (id: string) =>
    set(
      (state) => {
        const queueIds = new Set(state.ui.assessmentSidebarIds);
        if (!queueIds.has(id)) return state;
        const selectedIds = state.ui.assessmentSidebarSelectedIds;
        const nextSelectedIds = selectedIds.includes(id)
          ? selectedIds.filter((item) => item !== id)
          : [...selectedIds, id];
        return {
          ui: {
            ...state.ui,
            assessmentSidebarSelectedIds: nextSelectedIds,
          },
        };
      },
      false,
      "ui/toggleAssessmentSidebarSelectedId",
    ),
  openAssessmentSidebar: () =>
    set(
      (state) => ({
        ui: {
          ...state.ui,
          assessmentSidebarOpen: true,
          assessmentSidebarCollapsed: false,
        },
      }),
      false,
      "ui/openAssessmentSidebar",
    ),
  closeAssessmentSidebar: () =>
    set(
      (state) => ({
        ui: {
          ...state.ui,
          assessmentSidebarCollapsed: true,
        },
      }),
      false,
      "ui/closeAssessmentSidebar",
    ),
  toggleAssessmentSidebarCollapsed: () =>
    set(
      (state) => ({
        ui: {
          ...state.ui,
          assessmentSidebarCollapsed: !state.ui.assessmentSidebarCollapsed,
        },
      }),
      false,
      "ui/toggleAssessmentSidebarCollapsed",
    ),
  setUserRole: (userRole) =>
    set(
      (state) => ({
        ui: {
          ...state.ui,
          userRole,
        },
      }),
      false,
      "ui/setUserRole",
    ),
  setPrivileges: (privileges: string[]) =>
    set(
      (state) => ({
        ui: {
          ...state.ui,
          privileges,
          privilegesLoaded: true,
        },
      }),
      false,
      "ui/setPrivileges",
    ),
});
